import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {Button, CircularProgress, Grid, IconButton, Typography, makeStyles} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import {observer} from 'mobx-react-lite';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {formatCurrency} from '../../../Core/helpers/formatCurrency';
import {getApprovalRating} from '../../../Core/helpers/utils/ratingMapping';
import {useAgeniumUser} from '../../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {IS_PRODUCTION} from '../../../constants';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {Outlist} from '../InstantAppModal';
import {ProsperFormValues} from '../ProductTabs/lib/validation';
import FormField, {FormFieldType} from '../ProductTabs/ui/FormField';
import {updateHubspotProsperDealDecisionApproved} from '../deal';
import {useCommonModalStyles} from './common/modalStyles';
import {useInstantApp} from './common/useInstantApp';
import {getAgeniumCalcfocusRequest, getAgeniumFieldsForDeal, getApprovalQuoteFromCalcfocus} from './utils';
import {validateIllustration} from '../constants';
import {useApplicationData} from '@/Core/hooks/useApplicationData';

interface ApprovalInformationProps {
    dialogContentRef: React.RefObject<unknown>;
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onBackToClients?: () => void;
    onBack?: () => void;
    onNext?: () => void;
    calcFocusDataLocal?: {
        faceAmount?: number;
        monthlyPremium?: number;
        lapseAge?: number;
        quoteAge?: number;
        productName?: string;
        req?: any;
        out?: any;
    };
    setCalcFocusDataLocal?: React.Dispatch<
        React.SetStateAction<{
            faceAmount?: number;
            monthlyPremium?: number;
            lapseAge?: number;
            quoteAge?: number;
            productName?: string;
            req?: any;
            out?: any;
        }>
    >;
    ageniumContext: AgenuimContextType;
}

type quoteValues = {
    prosperAmplifySolves:
        | 'EasySolve'
        | 'SureSolve'
        | 'Optimize Cash Accumulation'
        | 'Optimize Income'
        | 'Optimize Death Benefit';
    prosperSpecify: 'Premium' | 'Coverage';
    prosperAmount: string | number;
};

const useStyles = makeStyles(() => ({
    tableHeader: {
        padding: '4px 0',
        fontFamily: 'FredokaRegular',
        fontWeight: 700,
        fontSize: '17.29px',
        lineHeight: '20.92px',
        color: '#838383',
        textTransform: 'uppercase',
    },
    tableContent: {
        padding: '4px 0',
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '30px',
        lineHeight: '38.4px',
        color: '#434343',
    },
    tableSmallContent: {
        padding: '4px 0',
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '23.04px',
        color: '#434343',
    },
    updateButton: {
        minWidth: 100,
        position: 'relative',
        color: '#3f51b5',
        textTransform: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    buttonProgress: {
        marginRight: 8,
        color: '#3f51b5',
    },
    buttonProgress2: {
        marginRight: 6,
    },
    buttonContainer: {
        position: 'relative',
    },
    warningText: {
        color: '#d32f2f',
        fontSize: '14px',
        marginTop: '8px',
        fontWeight: 500,
    },
    downloadIllustration: {
        fontFamily: 'FredokaRegular',
        fontSize: '16px',
        textDecoration: 'none',
        color: '#3f51b5',
        '& .MuiSvgIcon-root': {marginBottom: '-5px'},
    },
}));

type PrintReadyState = Outlist[][];

const ApprovalInformation: React.FC<ApprovalInformationProps> = observer(
    ({
        dialogContentRef,
        parameterStore,
        contactFormStore,
        onBack,
        onExit,
        onBackToClients,
        onNext,
        calcFocusDataLocal,
        setCalcFocusDataLocal,
        ageniumContext,
    }) => {
        const {
            formState: {errors},
        } = useFormContext<ProsperFormValues>();

        const classes = useStyles();
        const {approve} = useInstantApp();
        const {ageniumUser} = useAgeniumUser();
        const {applicationData} = useApplicationData();
        const commonClasses = useCommonModalStyles();
        const [readyForPrint, setReadyForPrint] = useState<PrintReadyState>([]);
        const [isLoading, setIsLoading] = useState(false);
        const [isNextLoading, setIsNextLoading] = useState(false);
        const [scrollTopRequested, setScrollTopRequested] = useState(false);
        const [deathBenefit, setDeathBenefit] = useState<number>(0);
        const parameters = parameterStore.quote;
        const [curApprovedSlide, setCurApprovedSlide] = useState(0);
        const {onValueChangedQuote: valueChanged} = parameterStore;
        const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
        const [is1HrWaiting, setIs1HrWaiting] = useState(
            parameterStore.approval.decisionLater1HrMark.value + 3600000 > Date.now(),
        );
        const [waitTimeLeft, setWaitTimeLeft] = useState(
            Math.ceil((parameterStore.approval.decisionLater1HrMark.value + 3600000 - Date.now()) / 60000) + 1,
        );
        const savedProsperData = applicationData?.prosperData;

        // Prioritize saved prosper data over parameters for init
        const [updateQuoteValues, setUpdateQuoteValues] = useState<quoteValues[]>([
            {
                prosperAmplifySolves: savedProsperData?.prosperAmplifySolves || parameters.prosperAmplifySolves.value,
                prosperSpecify: savedProsperData?.prosperSpecify || parameters.prosperSpecify.value,
                prosperAmount: savedProsperData?.prosperAmount || parameters.prosperAmount.value,
            },
        ]);
        const hasCalledHubspotUpdateRef = useRef(false);

        function getMaxFaceAmount() {
            return Number(
                ageniumContext.applicationData?.find((t) => t.key === 'Case_FinancialCalc_MaxFaceAmount')?.value,
            );
        }

        useEffect(() => {
            if (!is1HrWaiting && calcFocusDataLocal?.out) {
                const latestsDeathBenefit = calcFocusDataLocal.out.find(
                    (o: Outlist) => o.name === 'Initial death benefit',
                )?.value;

                setDeathBenefit(latestsDeathBenefit);
            }
        }, [calcFocusDataLocal, is1HrWaiting]);

        useEffect(() => {
            if (!is1HrWaiting && setCalcFocusDataLocal) setCalcFocusDataLocal({});
        }, [ageniumContext.applicationData, parameters, is1HrWaiting]);

        useEffect(() => {
            const loadInitialData = async () => {
                if (
                    !is1HrWaiting &&
                    !isInitialDataLoaded &&
                    onNext &&
                    ageniumContext.applicationData?.length &&
                    readyForPrint.length === 0
                )
                    await getAndPrintQuote(savedProsperData);
            };
            loadInitialData();
        }, [ageniumContext.applicationData, onNext, is1HrWaiting, isInitialDataLoaded]);

        // Call updateHubspotProsperDealDecisionApproved once when we have agenium data
        useEffect(() => {
            const callHubspotUpdate = async () => {
                if (ageniumContext.applicationData?.length && !hasCalledHubspotUpdateRef.current) {
                    hasCalledHubspotUpdateRef.current = true;
                    await updateHubspotProsperDealDecisionApproved(ageniumContext.applicationData);
                }
            };
            callHubspotUpdate();
        }, [ageniumContext.applicationData]);

        useEffect(() => {
            let waitTimeCounter: string | number | NodeJS.Timeout | undefined;
            let intCounter = waitTimeLeft;
            if (!is1HrWaiting) {
                ageniumContext.initSdk();
                if (parameterStore.approval.decisionLater1HrMark.value) {
                    parameterStore.approval.decisionLater1HrMark.setValue(0);
                    contactFormStore.saveForm();
                }
            } else {
                waitTimeCounter = setInterval(() => {
                    if (intCounter < 2) {
                        parameterStore.approval.decisionLater1HrMark.setValue(0);
                        contactFormStore.saveForm();
                        setIs1HrWaiting(false);
                        ageniumContext.initSdk();
                        clearInterval(waitTimeCounter);
                    } else {
                        setWaitTimeLeft((prev) => prev - 1);
                        intCounter--;
                    }
                }, 60000);
            }
            return () => {
                clearInterval(waitTimeCounter);
            };
        }, []);

        useEffect(() => {
            if (dialogContentRef.current && scrollTopRequested) {
                setScrollTopRequested(false);
                (dialogContentRef.current as Element)?.scrollTo({top: 0});
            }
        }, [dialogContentRef, scrollTopRequested]);

        async function getAndPrintQuote(savedProsperData?: any) {
            if (isLoading) {
                return;
            }
            setIsLoading(true);
            try {
                const uwdecision = {
                    name: 'Underwriting Decision',
                    value: ageniumContext?.applicationData?.find((t) => t.key === 'Case_UWDecision_Overall')?.value,
                    type: 'string',
                };

                const calcFocusResp = await getApprovalQuoteFromCalcfocus(
                    ageniumContext.applicationData as ApplicationData[],
                    ageniumUser,
                    savedProsperData,
                );

                if (calcFocusResp?.out) {
                    const cleanedOutList = calcFocusResp.out.filter((out) => out.fieldName !== 'plannedAnnualPremium');
                    const tableRating = ageniumContext.applicationData?.find(
                        (t) => t.key === 'Case_UWDecision_HxOutcome_TableRating',
                    )?.value;

                    if (tableRating) {
                        cleanedOutList.splice(0, 0, {
                            name: 'Table Rating',
                            value: tableRating,
                            type: 'string',
                            fieldName: 'tableRating',
                        });
                    }

                    setReadyForPrint((prev) => {
                        const newList = [...prev, ...(cleanedOutList ? [[uwdecision, ...cleanedOutList]] : [])];
                        setCurApprovedSlide(newList.length - 1);
                        return newList;
                    });

                    if (
                        setCalcFocusDataLocal &&
                        JSON.stringify(calcFocusDataLocal?.out) !== JSON.stringify(calcFocusResp?.out)
                    ) {
                        setCalcFocusDataLocal(calcFocusResp);
                    }
                }
            } catch (error) {
                console.error('Error in getAndPrintQuote:', error);
            } finally {
                console.log(' getAndPrintQuote completed');
                setIsLoading(false);
                setIsInitialDataLoaded(true);
            }
        }

        async function getAndPrintUpdatedQuote() {
            await getAndPrintQuote();
            const newQuoteValues = {
                prosperAmplifySolves: parameters.prosperAmplifySolves.value,
                prosperSpecify: parameters.prosperSpecify.value,
                prosperAmount: parameters.prosperAmount.value,
            };
            setUpdateQuoteValues((prev) => [...prev, newQuoteValues]);
            setScrollTopRequested(true);
        }

        const handleUpdateQuote = async () => {
            await getAndPrintUpdatedQuote();
        };

        function formatValueByType(value: string | number | number[], type: string) {
            switch (type) {
                case 'currency':
                    return value == undefined ? value : formatCurrency(value as number);
            }
            if (typeof value === 'string' && value?.includes('https://'))
                return (
                    <a href={value} target="_blank" className={classes.downloadIllustration}>
                        <SaveAltIcon /> Download Illustration
                    </a>
                );
            return value;
        }

        function slideSidingStyle() {
            if (readyForPrint.length < 2) return 'unset';
            return `${curApprovedSlide === 0 ? 3 : -(curApprovedSlide < 5 ? curApprovedSlide : 5)}px 0 0 rgb(63,81,181)`;
        }

        async function callCalcFocusThenNext() {
            const calcFocusResp = await getApprovalQuoteFromCalcfocus(
                ageniumContext.applicationData as ApplicationData[],
                ageniumUser,
            );

            if (calcFocusResp?.out) {
                if (setCalcFocusDataLocal) setCalcFocusDataLocal(calcFocusResp);
                if (onNext) {
                    onNext();
                    setIsNextLoading(false);
                } else setIsNextLoading(false);
            }
        }

        async function handleNext() {
            if (is1HrWaiting) {
                if (onBackToClients) onBackToClients();
                setIsNextLoading(false);
            } else if (onNext) {
                setIsNextLoading(true);

                if (curApprovedSlide > 0) {
                    parameters.prosperAmplifySolves.setValue(
                        updateQuoteValues[curApprovedSlide + 1].prosperAmplifySolves,
                    );
                    parameters.prosperSpecify.setValue(updateQuoteValues[curApprovedSlide + 1].prosperSpecify);
                    parameters.prosperAmount.setValue(updateQuoteValues[curApprovedSlide + 1].prosperAmount);

                    if (ageniumContext.applicationData) {
                        const selectedSlideData = readyForPrint[curApprovedSlide];
                        // Extract calcfocus response data from the selected slide
                        const faceAmount = selectedSlideData?.find(
                            (item) => item.name === 'Initial death benefit',
                        )?.value;

                        const monthlyPremium = selectedSlideData?.find(
                            (item) => item.fieldName === 'monthlyPremium',
                        )?.value;

                        const inputData = getAgeniumCalcfocusRequest(
                            ageniumContext.applicationData as ApplicationData[],
                            ageniumUser,
                        );
                        const updateData = {
                            ageniumFields: getAgeniumFieldsForDeal(ageniumContext.applicationData),
                            calcfocusResponse: {
                                out: selectedSlideData,
                                faceAmount: faceAmount,
                                monthlyPremium: monthlyPremium,
                            },
                            prosperData: inputData,
                        };

                        await ageniumContext.updateApplicationData(updateData, parameterStore.formid);
                        await updateHubspotProsperDealDecisionApproved(ageniumContext.applicationData);
                        await contactFormStore.saveForm();

                        // Update local calcfocus data to match selected slide
                        if (setCalcFocusDataLocal) {
                            setCalcFocusDataLocal({
                                out: selectedSlideData,
                                faceAmount: typeof faceAmount === 'number' ? faceAmount : Number(faceAmount),
                                monthlyPremium:
                                    typeof monthlyPremium === 'number' ? monthlyPremium : Number(monthlyPremium),
                            });
                        }
                    }

                    // Continue to next step
                    if (onNext) {
                        onNext();
                        setIsNextLoading(false);
                    }
                } else {
                    approve();
                    onNext();
                    setIsNextLoading(false);
                }
            }
        }

        function returnReadyToPrintMapValue(key: string, defaultValue: any) {
            const seekingResult = readyForPrint[curApprovedSlide]?.find((q) => q.name === key)?.value;
            return seekingResult != null ? seekingResult : defaultValue;
        }

        // Calculate approval rating
        const approvalRating = getApprovalRating(ageniumContext.applicationData, parameterStore);

        let quoteValid = returnReadyToPrintMapValue('Valid', 'invalid') == 'valid';
        if (
            calcFocusDataLocal &&
            calcFocusDataLocal.lapseAge &&
            calcFocusDataLocal.quoteAge &&
            calcFocusDataLocal.monthlyPremium &&
            calcFocusDataLocal.faceAmount
        ) {
            quoteValid = validateIllustration(
                calcFocusDataLocal.monthlyPremium,
                calcFocusDataLocal.faceAmount,
                calcFocusDataLocal.lapseAge,
                calcFocusDataLocal.quoteAge!,
            );
        }

        return (
            <Grid container className={commonClasses.wrapper}>
                <Typography className={commonClasses.title}>Approval Information</Typography>
                <Grid container>
                    <Grid item xs={9}>
                        <Grid container style={{height: '48px'}}>
                            <Grid item xs={8}>
                                <Typography className={commonClasses.text}>
                                    Your client has been approved {approvalRating ? `[${approvalRating}]` : ''}
                                </Typography>
                            </Grid>
                            {readyForPrint.length > 1 && (
                                <Grid item xs={4} style={{textAlign: 'right'}}>
                                    <IconButton
                                        aria-label="Previous Approval Information"
                                        disabled={curApprovedSlide === 0}
                                        onClick={() => setCurApprovedSlide((prev) => (prev - 1 < 0 ? 0 : prev - 1))}
                                    >
                                        <ArrowBackIosIcon fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                        aria-label="Next Approval Information"
                                        disabled={readyForPrint.length - 1 === curApprovedSlide}
                                        onClick={() =>
                                            setCurApprovedSlide((prev) =>
                                                prev + 1 > readyForPrint.length - 1
                                                    ? readyForPrint.length - 1
                                                    : prev + 1,
                                            )
                                        }
                                    >
                                        <ArrowForwardIosIcon fontSize="inherit" />
                                    </IconButton>
                                </Grid>
                            )}
                        </Grid>
                        {is1HrWaiting ? (
                            <Typography className={commonClasses.boldBlueText} style={{marginTop: 60}}>
                                Check back later at your appointment time. Approval Information will be available in{' '}
                                {waitTimeLeft > 59 ? '1-hour' : `${waitTimeLeft} minute${waitTimeLeft > 1 ? 's' : ''}`}.
                            </Typography>
                        ) : readyForPrint.length ? (
                            <Grid
                                container
                                style={
                                    readyForPrint.length > 1
                                        ? {
                                              margin: '10px 0 40px',
                                              padding: '10px 20px',
                                              border: '2px solid #3f51b5',
                                              borderRadius: '12px',
                                              boxShadow: slideSidingStyle(),
                                          }
                                        : {marginTop: '10px', marginBottom: '40px'}
                                }
                                spacing={3}
                            >
                                <Grid item xs={8}>
                                    <Typography component="span" className={commonClasses.boldtext}>
                                        {curApprovedSlide === 0
                                            ? 'Original Applied For Quote'
                                            : 'Approved Policy Details'}{' '}
                                        {curApprovedSlide !== 0 &&
                                            readyForPrint.length &&
                                            `- Modified #${curApprovedSlide}`}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} style={{textAlign: 'right'}}>
                                    {readyForPrint[curApprovedSlide].map(
                                        (item, index) =>
                                            !Array.isArray(item.value) &&
                                            item.name.toLowerCase() === 'illustration' && (
                                                <Fragment key={`ic-${index}`}>
                                                    {formatValueByType(item.value, item.type)}
                                                </Fragment>
                                            ),
                                    )}
                                </Grid>
                                <Grid item container spacing={4}>
                                    {readyForPrint[curApprovedSlide].map(
                                        (item, itemIndex) =>
                                            !Array.isArray(item.value) &&
                                            item.name.toLowerCase() !== 'illustration' &&
                                            item.name.toLowerCase() !== 'valid' && (
                                                <Grid item xs={4} key={`0-${itemIndex}`}>
                                                    <Typography className={classes.tableHeader}>{item.name}</Typography>
                                                    <Typography className={classes.tableContent}>
                                                        {formatValueByType(item.value, item.type)}
                                                    </Typography>
                                                </Grid>
                                            ),
                                    )}
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid item container>
                                Loading... Please Wait
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {readyForPrint.length !== 0 && (
                    <Grid container style={{margin: '40px 0', border: '2px solid transparent'}} spacing={3}>
                        <Grid item xs={12}>
                            <Typography component="span" className={commonClasses.boldtext}>
                                Need to modify the quote?
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <FormField
                                name="prosperAmplifySolves"
                                label="Amplify Solves"
                                formFieldType={FormFieldType.SELECT}
                                options={[
                                    'OptimalSolve',
                                    'EasySolve',
                                    'SureSolve',
                                    'Optimize Cash Accumulation',
                                    'Optimize Income',
                                    'Optimize Death Benefit',
                                ]}
                                optionValues={[
                                    'OptimalSolve',
                                    'EasySolve',
                                    'SureSolve',
                                    'OptimizeCashAccumulation',
                                    'OptimizeIncome',
                                    'OptimizeDeathBenefit',
                                ]}
                                defaultValue={savedProsperData?.prosperAmplifySolves}
                                parameter={parameters.prosperAmplifySolves}
                                errors={!!errors.prosperAmplifySolves}
                                helperText={errors.prosperAmplifySolves?.message}
                                disabled
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormField
                                name="prosperSpecify"
                                label="Specify"
                                formFieldType={FormFieldType.SELECT}
                                options={['Premium', 'Coverage']}
                                optionValues={['Premium', 'Coverage']}
                                defaultValue={savedProsperData?.prosperSpecify}
                                parameter={parameters.prosperSpecify}
                                errors={!!errors.prosperSpecify}
                                helperText={errors.prosperSpecify?.message}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <FormField
                                name="prosperAmount"
                                label="Amount"
                                formFieldType={FormFieldType.CURRENCY}
                                defaultValue={savedProsperData?.prosperAmount}
                                parameter={parameters.prosperAmount}
                                errors={!!errors.prosperAmount}
                                helperText={errors.prosperAmount?.message}
                                valueChanged={valueChanged}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Button
                                variant="text"
                                type="submit"
                                onClick={handleUpdateQuote}
                                disabled={isLoading}
                                className={classes.updateButton}
                            >
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={20} className={classes.buttonProgress} />
                                        Updating
                                    </>
                                ) : (
                                    <>
                                        <CheckCircleOutlineIcon className={classes.buttonProgress2} />
                                        Update Quote
                                    </>
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={2} justify="space-around" className={commonClasses.navigation}>
                    <Grid item xs={2}>
                        <Button
                            color="primary"
                            variant="text"
                            onClick={onExit}
                            startIcon={<KeyboardBackspaceIcon />}
                            className={commonClasses.backBtn}
                        >
                            Save and exit
                        </Button>
                    </Grid>
                    {onBack && (
                        <Grid item className={commonClasses.btnWrapper}>
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={onBack}
                                className={commonClasses.nextBtn}
                            >
                                BACK
                            </Button>
                        </Grid>
                    )}
                    <Grid item className={commonClasses.btnWrapper}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleNext}
                            className={commonClasses.nextBtn}
                            disabled={
                                isNextLoading ||
                                (!is1HrWaiting &&
                                    (!ageniumContext.applicationData ||
                                        isLoading ||
                                        !(
                                            returnReadyToPrintMapValue('Initial death benefit', 1000000000) <=
                                            Number(getMaxFaceAmount())
                                        ) ||
                                        !quoteValid))
                            }
                        >
                            {is1HrWaiting ? (
                                'Back to Clients'
                            ) : (
                                <>
                                    {isNextLoading ? (
                                        <>
                                            <CircularProgress size={16} color="inherit" />
                                            <span style={{marginLeft: '10px'}}>Next</span>
                                        </>
                                    ) : (
                                        'Next'
                                    )}
                                </>
                            )}
                        </Button>
                        {returnReadyToPrintMapValue('Initial death benefit', 1000000000) > Number(getMaxFaceAmount()) &&
                            !isLoading && (
                                <Typography className={classes.warningText}>
                                    The face amount is greater than the max approved face amount{' '}
                                    {formatCurrency(getMaxFaceAmount())}
                                </Typography>
                            )}
                        {!quoteValid && !isLoading && (
                            <Typography className={classes.warningText}>
                                Either the monthly premium is less than the minimum amount required, initial death
                                benefit is less than the minimum coverage required, or the policy lapses before year 15.
                            </Typography>
                        )}
                        {quoteValid &&
                            calcFocusDataLocal?.lapseAge &&
                            calcFocusDataLocal?.lapseAge < 121 &&
                            !isLoading && (
                                <Typography className={classes.warningText}>
                                    This policy will lapse prior to age 121. Please confirm with the client.
                                </Typography>
                            )}
                    </Grid>
                </Grid>
                {!IS_PRODUCTION && (
                    <div style={{fontSize: '10px', marginTop: 50}}>
                        <div>applicationData</div>
                        <div style={{color: '#880'}}>{JSON.stringify(ageniumContext.applicationData, null, 2)}</div>
                        <div>calcFocusDataLocal</div>
                        <div style={{color: '#808'}}>{JSON.stringify(calcFocusDataLocal, null, 2)}</div>
                    </div>
                )}
            </Grid>
        );
    },
);

export default ApprovalInformation;
